import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { MaskedLines, Reveal, ChapterNumber } from "../components/motion";
import { MarqueeBand } from "../components/MarqueeBand";
import { IMAGES, services } from "../data/content";

const ServiceChapter = ({ n, image, imageAlt, data, flipped, testid }) => (
  <section className={`${flipped ? "bg-sand/70" : ""}`} data-testid={testid}>
    <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-32">
      <Reveal className={`lg:col-span-5 ${flipped ? "lg:order-2 lg:col-start-8" : ""}`}>
        <div className="clipped-frame overflow-hidden">
          <img
            src={image}
            alt={imageAlt}
            className="h-[380px] w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[560px]"
            data-testid={`${testid}-image`}
          />
        </div>
      </Reveal>
      <div className={`lg:col-span-6 ${flipped ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
        <Reveal>
          <ChapterNumber n={n} />
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
            {data.title}
          </h2>
          <p className="mt-6 text-base leading-relaxed text-ink/70">{data.intro}</p>
        </Reveal>
        <div className="mt-10 space-y-0">
          {data.items.map((item, i) => (
            <Reveal key={item.title} delay={0.05 * i}>
              <div
                className={`flex gap-4 border-t border-border py-5 ${i === data.items.length - 1 ? "border-b" : ""}`}
                data-testid={`${testid}-item-${i}`}
              >
                <Check className="mt-1 h-4 w-4 shrink-0 text-gold" strokeWidth={2.5} />
                <div>
                  <h3 className="font-serif text-xl font-medium tracking-tight text-forest">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/65">{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <Link
            to="/contact?type=consultation"
            data-testid={`${testid}-cta`}
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
          >
            Talk It Through With Me
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="mx-auto max-w-7xl px-6 pb-8 pt-36 lg:px-10 lg:pt-44" data-testid="services-hero">
        <h1 className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="services-kicker">
          Group Health, PEO &amp; Individual Coverage Services
        </h1>
        <MaskedLines
          as="p"
          className="mt-8 font-serif text-5xl font-light leading-[0.95] tracking-tighter text-forest sm:text-6xl lg:text-7xl"
          lines={["Benefits design,", "from soil to table."]}
        />
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
            Health benefits are a crop that needs tending — for the family choosing a plan, and for the
            employer funding one. I do the tending with you.
          </p>
        </Reveal>
      </section>

      <ServiceChapter
        n="01"
        testid="services-individuals"
        image={IMAGES.family}
        imageAlt="Family outdoors in warm light"
        data={services.individuals}
      />
      <ServiceChapter
        n="02"
        testid="services-employers"
        image={IMAGES.team}
        imageAlt="Professional team collaborating in a warm office"
        data={services.employers}
        flipped
      />

      <MarqueeBand items={["Design", "Educate", "Enroll", "Review", "Repeat"]} />

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="how-we-work">
        <Reveal>
          <ChapterNumber n="03" />
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
            How I <em className="text-terra">work with you</em>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {[
            ["Listen", "A relaxed first conversation about your household or your team — doctors, prescriptions, budget, worries. No pitch, just picture-taking."],
            ["Compare", "I lay real options side by side — premiums, deductibles, networks, worst-case numbers — and translate every trade-off into plain language."],
            ["Tend", "Enrollment is the beginning, not the end. I stay on call through every season — life changes, claims questions, and an annual review before every renewal."],
          ].map(([title, body], i) => (
            <Reveal key={title} delay={0.08 * i}>
              <div
                className="h-full border border-border p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60 lg:p-10"
                data-testid={`how-we-work-step-${i + 1}`}
              >
                <span className="font-mono text-xs tracking-[0.35em] text-terra">0{i + 1}</span>
                <h3 className="mt-5 font-serif text-2xl font-medium tracking-tight text-forest">{title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-ink/65">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
